import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "content" }
const _hoisted_2 = { class: "section-header" }
const _hoisted_3 = {
  key: 0,
  class: "section"
}
const _hoisted_4 = { class: "section-header" }
const _hoisted_5 = {
  key: 1,
  class: "section"
}
const _hoisted_6 = { class: "section-sub-header" }
const _hoisted_7 = { class: "section-body" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = {
  key: 2,
  class: "horizontal-divider"
}
const _hoisted_10 = { class: "section-header" }
const _hoisted_11 = {
  key: 4,
  class: "section"
}
const _hoisted_12 = { class: "section-sub-header" }
const _hoisted_13 = {
  key: 1,
  class: "no-connection-text"
}

import TextFieldStandard from '@/design-system/atoms/TextFieldStandard.vue'
import TextAreaStandard from '@/design-system/atoms/TextAreaStandard.vue'
import IconFolderMedium from '@/design-system/assets/icons/IconFolderMedium.vue'
import IconFileMedium from '@/design-system/assets/icons/IconFileMedium.vue'
import IconLinkMedium from '@/design-system/assets/icons/IconLinkMedium.vue'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import SimpleFormModal from './SimpleFormModal.vue'
import { computed, ref, toRefs, watch } from 'vue'
import ChevronToggle from '@/design-system/atoms/ChevronToggle.vue'
import type { DirectoryItem } from './types'
import { useI18n } from 'vue-i18n'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import { useCoreEntityStore } from '@/pinia/coreEntity'
import IconMoveFolderSmall from '@/design-system/assets/icons/IconMoveFolderSmall.vue'
import IconMarkerPin from '@/design-system/assets/icons/IconMarkerPin.vue'
import { validateInput, Input } from './inputValidation'

type Props = {
  open?: boolean
  selectedItem: DirectoryItem
  breadcrumbs: DirectoryItem[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EditModal',
  props: {
    open: { type: Boolean, default: false },
    selectedItem: {},
    breadcrumbs: {}
  },
  emits: ["submit", "close", "openConnection", "openMove"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const { selectedItem } = toRefs(props)
const { t } = useI18n()

const values = ref<Record<string, any>>({})
const initialValues = ref<Record<string, any>>({})
const inputs: Input[] = [
  {
    key: 'name',
    label: t('general__text__name'),
    prefilledText: selectedItem.value.name,
    required: true,
    type: 'text',
    validations: [
      {
        validator: (value) => value !== '',
        message: t('general__text__missing-name')
      },
      {
        validator: (value) => typeof value === 'string' && value.length <= 255,
        message: t('general__data__file-name-too-long')
      }
    ]
  },
  {
    key: 'url',
    label: 'URL',
    prefilledText: selectedItem.value.type === 'link' ? selectedItem.value.url : '',
    required: selectedItem.value.type === 'link',
    type: 'text',
    validations: [
      {
        validator: (value) => value !== '',
        message: `${t('general__text__missing-url')}, ${t(
          'general__text__valid-format'
        ).toLowerCase()} 'https://www.example.com'`
      },
      {
        validator: (value) => {
          const urlPattern = /^(https:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w- ./?%&=]*)?$/i
          return urlPattern.test(value as string)
        },
        message: `${t('general__text__invalid-url')}, ${t(
          'general__text__valid-format'
        ).toLowerCase()} 'https://www.example.com'`
      }
    ],
    helpText: `${t('general__text__valid-format')} 'https://www.example.com'`
  },
  {
    key: 'description',
    label: t('general__text__description'),
    prefilledText: selectedItem.value.description
  }
]
const openInputSection = ref(true)
const openWaySection = ref(false)
const openObjectSection = ref(false)
const iconComponent = ref<any>(null)

const emit = __emit

const isOpen = ref(props.open)
const parentNames = ref<Record<string, string>>({})
const submitButtonText = computed(() => t(`general__text__update-${selectedItem.value.type}`))
const coreEntityStore = useCoreEntityStore()

const validationResults = computed<Record<string, { result: boolean; message?: string }>>(() => {
  const results: Record<string, { result: boolean; message?: string }> = {}
  inputs.forEach((input) => {
    const value = values.value[input.key]
    if (value !== undefined) {
      const validationResult = validateInput(input, value)
      results[input.key] = validationResult
    } else {
      results[input.key] = { result: true }
    }
  })
  return results
})

async function getCoreEntityNames(path: string[] | undefined): Promise<string | undefined> {
  if (!path || path.length === 0) return undefined
  const names: string[] = []
  for (const id of path) {
    const entity = await coreEntityStore.getCoreEntity(id)
    if (entity) names.push(entity.displayName)
  }
  return names.length ? names.join(', ') : undefined
}

const getIconComponentByType = (type: DirectoryItem['type']) => {
  switch (type) {
    case 'directory':
      return IconFolderMedium
    case 'file':
      return IconFileMedium
    case 'link':
      return IconLinkMedium
    default:
      return IconFileMedium
  }
}

watch(
  selectedItem,
  (item) => {
    if (item) {
      iconComponent.value = getIconComponentByType(item.type)
      values.value = { ...item } as Record<string, any>
      initialValues.value = JSON.parse(JSON.stringify(values.value))
    } else {
      iconComponent.value = null
    }
  },
  { immediate: true }
)

const hasChanges = computed(
  () =>
    Object.values(values.value)
      .filter((v) => (v === null || v === undefined ? '' : v))
      .toSorted()
      .join() !==
    Object.values(initialValues.value)
      .filter((v) => (v === null || v === undefined ? '' : v))
      .toSorted()
      .join()
)

const submitDisabled = computed(() => {
  const missingRequired =
    !values.value.name || (selectedItem.value.type === 'link' && !values.value.url)
  return missingRequired || !hasChanges.value
})

watch(
  () => props.open,
  (val) => {
    isOpen.value = val
  },
  { immediate: true }
)

watch(
  selectedItem,
  async (item) => {
    if (!item) return
    const names = await getCoreEntityNames(item.entityConnectionPath)
    if (names) parentNames.value[item.id] = names
  },
  { immediate: true }
)

const handleInput = (key: string, input: string | number) => {
  values.value[key] = input
}

const handleSubmit = () => {
  if (!submitDisabled.value) {
    isOpen.value = false
    emit('submit', selectedItem.value, values.value)
  }
}

const handleClose = () => {
  openInputSection.value = true
  isOpen.value = false
  emit('close')
}

const openConnection = () => {
  emit('openConnection')
}

const openMove = () => {
  emit('openMove')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(SimpleFormModal, {
    title: _unref(selectedItem).name,
    "submit-button-text": submitButtonText.value,
    icon: iconComponent.value,
    open: isOpen.value,
    "disable-submit": submitDisabled.value,
    onClose: handleClose,
    onSubmit: handleSubmit
  }, {
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          class: "section-header-container",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (openInputSection.value = !openInputSection.value))
        }, [
          _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)('general__text__general-information')), 1),
          _createVNode(ChevronToggle, {
            state: openInputSection.value === true ? 'up' : 'down',
            medium: true,
            "aria-hidden": "true",
            class: "chevron-toggle"
          }, null, 8, ["state"])
        ]),
        (openInputSection.value)
          ? (_openBlock(), _createElementBlock("section", _hoisted_3, [
              _createVNode(TextFieldStandard, {
                style: { width: '50%' },
                label: inputs[0].label,
                prefilledText: inputs[0].prefilledText,
                required: "",
                type: "text",
                focus: "",
                hideIconX: "",
                error: validationResults.value[inputs[0].key]?.result === false,
                "error-message": '* ' + validationResults.value[inputs[0].key]?.message,
                onEmitInput: _cache[1] || (_cache[1] = (value) => handleInput('name', value))
              }, null, 8, ["label", "prefilledText", "error", "error-message"]),
              (_unref(selectedItem).type === 'link')
                ? (_openBlock(), _createBlock(TextFieldStandard, {
                    key: 0,
                    style: { width: '50%' },
                    label: inputs[1].label,
                    prefilledText: inputs[1].prefilledText,
                    required: "",
                    type: "text",
                    hideIconX: "",
                    error: validationResults.value[inputs[1].key]?.result === false,
                    "error-message": '* ' + validationResults.value[inputs[1].key]?.message,
                    "help-text": `${_unref(t)('general__text__valid-format')} 'https://www.example.com'`,
                    onEmitInput: _cache[2] || (_cache[2] = (value) => handleInput('url', value))
                  }, null, 8, ["label", "prefilledText", "error", "error-message", "help-text"]))
                : _createCommentVNode("", true),
              _createVNode(TextAreaStandard, {
                label: _unref(t)('general__text__description'),
                "prefilled-text": _unref(selectedItem).description,
                onEmitInput: _cache[3] || (_cache[3] = (value) => handleInput('description', value))
              }, null, 8, ["label", "prefilled-text"])
            ]))
          : _createCommentVNode("", true),
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "horizontal-divider" }, null, -1)),
        _createElementVNode("div", {
          class: "section-header-container",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (openWaySection.value = !openWaySection.value))
        }, [
          _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)('general__text__directory_path')), 1),
          _createVNode(ChevronToggle, {
            state: openWaySection.value === true ? 'up' : 'down',
            medium: true,
            "aria-hidden": "true",
            class: "chevron-toggle"
          }, null, 8, ["state"])
        ]),
        (openWaySection.value)
          ? (_openBlock(), _createElementBlock("section", _hoisted_5, [
              _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)('general__text__directory_path')) + ": ", 1),
              _createElementVNode("span", _hoisted_7, [
                _createElementVNode("div", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.breadcrumbs, (breadcrumb, index) => {
                    return (_openBlock(), _createElementBlock("span", {
                      key: index,
                      class: "section-text"
                    }, [
                      _createElementVNode("span", null, _toDisplayString(breadcrumb.name), 1),
                      (index < _ctx.breadcrumbs.length - 1)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, "/"))
                        : _createCommentVNode("", true)
                    ]))
                  }), 128))
                ]),
                _createVNode(ButtonText, {
                  buttonText: _unref(t)('general__text__move'),
                  class: "text-button",
                  size: "small",
                  onOnClicked: openMove
                }, {
                  left: _withCtx(() => [
                    _createVNode(IconMoveFolderSmall)
                  ]),
                  _: 1
                }, 8, ["buttonText"])
              ])
            ]))
          : _createCommentVNode("", true),
        (_unref(selectedItem).type !== 'directory')
          ? (_openBlock(), _createElementBlock("div", _hoisted_9))
          : _createCommentVNode("", true),
        (_unref(selectedItem).type !== 'directory')
          ? (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: "section-header-container",
              onClick: _cache[5] || (_cache[5] = ($event: any) => (openObjectSection.value = !openObjectSection.value))
            }, [
              _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(t)('general__text__linked-objects')), 1),
              _createVNode(ChevronToggle, {
                state: openObjectSection.value === true ? 'up' : 'down',
                "aria-hidden": "true",
                medium: true,
                class: "chevron-toggle"
              }, null, 8, ["state"])
            ]))
          : _createCommentVNode("", true),
        (openObjectSection.value)
          ? (_openBlock(), _createElementBlock("section", _hoisted_11, [
              _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(t)('general__text__linked-objects')) + ": ", 1),
              (_unref(selectedItem) && parentNames.value[_unref(selectedItem).id])
                ? (_openBlock(), _createBlock(BadgeSquare, {
                    key: 0,
                    badgeText: parentNames.value[_unref(selectedItem).id],
                    grey: true,
                    size: "medium"
                  }, null, 8, ["badgeText"]))
                : _createCommentVNode("", true),
              (!parentNames.value[_unref(selectedItem).id])
                ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(_unref(t)('general__text__no-connection')), 1))
                : _createCommentVNode("", true),
              _createVNode(ButtonText, {
                buttonText: _unref(t)('general__text__change-connection'),
                class: "text-button",
                size: "small",
                onOnClicked: openConnection
              }, {
                left: _withCtx(() => [
                  _createVNode(IconMarkerPin)
                ]),
                _: 1
              }, 8, ["buttonText"])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["title", "submit-button-text", "icon", "open", "disable-submit"]))
}
}

})